// src/app/page.tsx
"use client";

import React, { useState, useRef, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { PuzzleScene } from "@/components/PuzzleScene";
import { NavButton } from "@/components/NavButton";
import { Win95Window } from "@/components/window";
import { ProjectsPanel } from "@/components/ProjectsPanel";
import { PROJECTS, isFolder } from "@/projectsConfig";
import type { ProjectDef, ProjectFolder, ProjectItem } from "@/projectsConfig";
import { CAMERA_POS, CAMERA_ZOOM_START, CAMERA_ZOOM_SOLVED, CAMERA_ZOOM_LERP_SPEED } from "@/components/puzzleConfig";
import { theme } from "@/styles/theme";
import { AboutPanel } from "@/components/AboutPanel";
import { ContactPanel } from "@/components/ContactPanel";
import { FollowPanel } from "@/components/FollowPanel";
import { PasswordPanel } from "@/components/PasswordPanel";
import { VideoPlayerPanel } from "@/components/VideoPlayerPanel";

function CameraZoom({ solved }: { solved: boolean }) {
  const { camera, invalidate } = useThree();
  useFrame((_, delta) => {
    const target = solved ? CAMERA_ZOOM_SOLVED : CAMERA_ZOOM_START;
    const next = camera.zoom + (target - camera.zoom) * (1 - Math.exp(-CAMERA_ZOOM_LERP_SPEED * delta));
    if (Math.abs(next - camera.zoom) > 0.01) {
      camera.zoom = next;
      camera.updateProjectionMatrix();
      invalidate();
    }
  });
  return null;
}

type Offset = { x: number; y: number };

// Preload models
useGLTF.preload("/models/puzzle.glb");
useGLTF.preload("/models/BG.glb");

/**
 * Small hook: true when viewport width <= breakpoint (default 768px)
 */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    update(); // initial
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Desktop-aware offset helper:
 * - Mobile: fixed upward offset from center → window opens higher.
 * - Desktop: finds a random offset above center, avoiding nav area
 *   and staying away from other open windows.
 */
function getOffset(isMobile: boolean, usedDesktopOffsets: Offset[] = []): Offset {
  if (typeof window === "undefined") {
    return { x: 0, y: 0 };
  }

  // MOBILE: keep your previous behavior
  if (isMobile) {
    const MOBILE_WINDOW_Y_OFFSET = -20; // tweak this to move higher/lower
    return { x: 0, y: MOBILE_WINDOW_Y_OFFSET };
  }

  // DESKTOP: search for a "free" spot above center
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // How far from center we allow windows to wander
  const maxX = Math.min(220, vw * 0.3);
  const maxY = Math.min(140, vh * 0.25);

  // Only open ABOVE center so we never overlap the nav area (which lives below)
  const minY = -maxY;      // highest (towards top of screen)
  const maxYAllowed = -40; // a bit above the center line

  // Minimum distance between window centers so they don't clump
  const MIN_DISTANCE = 160;

  const rand = (min: number, max: number) =>
    min + Math.random() * (max - min);

  const isFarEnough = (cand: Offset) =>
    usedDesktopOffsets.every((o) => {
      const dx = cand.x - o.x;
      const dy = cand.y - o.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      return dist >= MIN_DISTANCE;
    });

  // Try a bunch of random candidates, pick the first that doesn't clash
  for (let i = 0; i < 24; i++) {
    const candidate: Offset = {
      x: rand(-maxX, maxX),
      y: rand(minY, maxYAllowed),
    };
    if (isFarEnough(candidate)) {
      return candidate;
    }
  }

  // Fallback: if everything's crowded, just put it above center
  return { x: 0, y: (minY + maxYAllowed) * 0.5 };
}

// Helper to decide if a href is external
function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export default function Home() {
  const isMobile = useIsMobile();

  // ──────────────────────────────────────────────
  // Configurable NAV positions and spacing
  // ──────────────────────────────────────────────
  const NAV_BOTTOM_DESKTOP = "14%";
  const NAV_BOTTOM_MOBILE = "32%"; // closer to center on mobile

  const NAV_MARGIN_TOP_DESKTOP = 40;
  const NAV_MARGIN_TOP_MOBILE = 24; // smaller gap on mobile

  const NAV_GAP_DESKTOP = 32; // horizontal spacing between buttons on desktop
  const NAV_GAP_MOBILE = 10;  // vertical spacing between buttons on mobile

  const [proximity, setProximity] = useState(0);
  const [isSolving, setIsSolving] = useState(false);
  const [isSolved, setIsSolved] = useState(false);

  // Window visibility
  const [aboutOpen, setAboutOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);

  // Nav visual selection, decoupled from animation
  const [aboutSelected, setAboutSelected] = useState(false);
  const [projectsSelected, setProjectsSelected] = useState(false);
  const [contactSelected, setContactSelected] = useState(false);

  // Z-order
  const [nextZ, setNextZ] = useState(2000);
  const [aboutZ, setAboutZ] = useState(2000);
  const [projectsZ, setProjectsZ] = useState(2001);
  const [contactZ, setContactZ] = useState(2002);
  const [followZ, setFollowZ] = useState(2003);
  const [passwordZ, setPasswordZ] = useState(2004);
  const [videoPlayerZ, setVideoPlayerZ] = useState(2005);

  // Window anchors and offsets
  const [aboutAnchor, setAboutAnchor] = useState<DOMRect | null>(null);
  const [projectsAnchor, setProjectsAnchor] = useState<DOMRect | null>(null);
  const [contactAnchor, setContactAnchor] = useState<DOMRect | null>(null);
  const [followAnchor, setFollowAnchor] = useState<DOMRect | null>(null);
  const [passwordAnchor] = useState<DOMRect | null>(null); // centered popup
  const [videoPlayerAnchor] = useState<DOMRect | null>(null); // centered popup

  const [aboutOffset, setAboutOffset] = useState<Offset | null>(null);
  const [projectsOffset, setProjectsOffset] = useState<Offset | null>(null);
  const [contactOffset, setContactOffset] = useState<Offset | null>(null);
  const [followOffset, setFollowOffset] = useState<Offset | null>(null);
  const [passwordOffset, setPasswordOffset] = useState<Offset | null>(null);
  const [videoPlayerOffset, setVideoPlayerOffset] = useState<Offset | null>(null);

  // Close animations
  const [aboutCloseKey, setAboutCloseKey] = useState(0);
  const [projectsCloseKey, setProjectsCloseKey] = useState(0);
  const [contactCloseKey, setContactCloseKey] = useState(0);
  const [followCloseKey, setFollowCloseKey] = useState(0);
  const [passwordCloseKey, setPasswordCloseKey] = useState(0);
  const [videoPlayerCloseKey, setVideoPlayerCloseKey] = useState(0);

  // Projects selection
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    PROJECTS[0]?.id ?? null
  );

  // Project currently waiting for password
  const [pendingProject, setPendingProject] = useState<ProjectDef | null>(null);

  // Project selected for video playback
  const [selectedVideoProject, setSelectedVideoProject] = useState<ProjectDef | null>(null);

  // Dynamic folder windows
  type FolderWindowState = {
    folder: ProjectFolder;
    anchorRect: DOMRect | null;
    offset: Offset;
    zIndex: number;
    closeKey: number;
    selectedProjectId: string | null;
  };
  const [openFolderWindows, setOpenFolderWindows] = useState<Record<string, FolderWindowState>>({});

  // Nav button refs (for trails)
  const aboutBtnRef = useRef<HTMLButtonElement | null>(null);
  const projectsBtnRef = useRef<HTMLButtonElement | null>(null);
  const contactBtnRef = useRef<HTMLButtonElement | null>(null);

  const bringToFront = (
    which: "about" | "projects" | "contact" | "follow" | "password" | "videoPlayer" | string
  ) => {
    setNextZ((prev) => {
      const newZ = prev + 1;
      if (which === "about") setAboutZ(newZ);
      else if (which === "projects") setProjectsZ(newZ);
      else if (which === "contact") setContactZ(newZ);
      else if (which === "follow") setFollowZ(newZ);
      else if (which === "password") setPasswordZ(newZ);
      else if (which === "videoPlayer") setVideoPlayerZ(newZ);
      else {
        // Dynamic folder window
        setOpenFolderWindows((prev) => {
          const entry = prev[which];
          if (!entry) return prev;
          return { ...prev, [which]: { ...entry, zIndex: newZ } };
        });
      }
      return newZ;
    });
  };

  // ──────────────────────────────────────────────
  // Nav button handlers
  // ──────────────────────────────────────────────

  const handleProjectsClick = () => {
    if (projectsOpen) {
      setProjectsSelected(false);
      setProjectsCloseKey((k) => k + 1);
      return;
    }

    if (isMobile && aboutOpen) {
      setAboutCloseKey((k) => k + 1);
    }
    if (isMobile && (contactFormOpen || followOpen)) {
      setContactCloseKey((k) => k + 1);
      setFollowCloseKey((k) => k + 1);
    }

    const btn = projectsBtnRef.current;
    if (btn) {
      setProjectsAnchor(btn.getBoundingClientRect());
    } else {
      setProjectsAnchor(null);
    }

    const usedOffsets: Offset[] = [];
    if (!isMobile) {
      if (aboutOpen && aboutOffset) usedOffsets.push(aboutOffset);
      if (contactFormOpen && contactOffset) usedOffsets.push(contactOffset);
      if (followOpen && followOffset) usedOffsets.push(followOffset);
    }

    setProjectsOffset(getOffset(isMobile, usedOffsets));
    setProjectsOpen(true);
    setProjectsSelected(true);
    bringToFront("projects");
  };

  const handleAboutClick = () => {
    if (aboutOpen) {
      setAboutSelected(false);
      setAboutCloseKey((k) => k + 1);
      return;
    }

    if (isMobile && projectsOpen) {
      setProjectsCloseKey((k) => k + 1);
    }
    if (isMobile && (contactFormOpen || followOpen)) {
      setContactCloseKey((k) => k + 1);
      setFollowCloseKey((k) => k + 1);
    }

    const btn = aboutBtnRef.current;
    if (btn) {
      setAboutAnchor(btn.getBoundingClientRect());
    } else {
      setAboutAnchor(null);
    }

    const usedOffsets: Offset[] = [];
    if (!isMobile) {
      if (projectsOpen && projectsOffset) usedOffsets.push(projectsOffset);
      if (contactFormOpen && contactOffset) usedOffsets.push(contactOffset);
      if (followOpen && followOffset) usedOffsets.push(followOffset);
    }

    setAboutOffset(getOffset(isMobile, usedOffsets));
    setAboutOpen(true);
    setAboutSelected(true);
    bringToFront("about");
  };

  function getSeparatedFollowOffset(contactPos: Offset, usedOffsets: Offset[]): Offset {
    const BASE_MIN = 200;
    const EXTRA = 120;
    const minSeparation = BASE_MIN + Math.random() * EXTRA;
    const minSepSq = minSeparation * minSeparation;

    for (let i = 0; i < 24; i++) {
      const candidate = getOffset(false, usedOffsets);
      const dx = candidate.x - contactPos.x;
      const dy = candidate.y - contactPos.y;
      const distSq = dx * dx + dy * dy;
      if (distSq >= minSepSq) {
        return candidate;
      }
    }

    const SHIFT_BASE = minSeparation;
    const SHIFT_EXTRA = 80;
    const shift = SHIFT_BASE + Math.random() * SHIFT_EXTRA;
    const angle = Math.random() * Math.PI * 2;

    let fallback: Offset = {
      x: contactPos.x + shift * Math.cos(angle),
      y: contactPos.y + shift * Math.sin(angle),
    };

    if (typeof window !== "undefined") {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxX = Math.min(220, vw * 0.3);
      const maxY = Math.min(140, vh * 0.25);
      const minY = -maxY;
      const maxYAllowed = -40;

      fallback = {
        x: Math.max(-maxX, Math.min(maxX, fallback.x)),
        y: Math.max(minY, Math.min(maxYAllowed, fallback.y)),
      };
    }

    return fallback;
  }

  const handleContactClick = () => {
    const isAnyContactOpen = contactFormOpen || followOpen;

    if (isAnyContactOpen) {
      setContactSelected(false);
      setContactCloseKey((k) => k + 1);
      setFollowCloseKey((k) => k + 1);
      return;
    }

    if (isMobile && aboutOpen) {
      setAboutCloseKey((k) => k + 1);
    }
    if (isMobile && projectsOpen) {
      setProjectsCloseKey((k) => k + 1);
    }

    const btn = contactBtnRef.current;
    const anchorRect = btn ? btn.getBoundingClientRect() : null;
    setContactAnchor(anchorRect);
    setFollowAnchor(anchorRect);

    if (isMobile) {
      const baseOffset = getOffset(true);
      setContactOffset(baseOffset);
      setFollowOffset({
        x: baseOffset.x,
        y: baseOffset.y + 130,
      });
    } else {
      const usedOffsetsForFirst: Offset[] = [];
      if (aboutOpen && aboutOffset) usedOffsetsForFirst.push(aboutOffset);
      if (projectsOpen && projectsOffset) usedOffsetsForFirst.push(projectsOffset);

      const contactPos = getOffset(false, usedOffsetsForFirst);
      setContactOffset(contactPos);

      const usedForFollow = [...usedOffsetsForFirst, contactPos];
      const followPos = getSeparatedFollowOffset(contactPos, usedForFollow);
      setFollowOffset(followPos);
    }

    setContactFormOpen(true);
    setFollowOpen(true);
    setContactSelected(true);
    bringToFront("contact");
    bringToFront("follow");
  };

  // ──────────────────────────────────────────────
  // Project play handler (password gate + new tab)
  // ──────────────────────────────────────────────

  const handleProjectPlayRequest = (project: ProjectDef) => {
    if (!project.href) return;

    if (project.requiresCode && project.codeHash) {
      setPendingProject(project);
      setPasswordOffset(isMobile ? { x: 0, y: -10 } : { x: 0, y: -40 });
      setPasswordOpen(true);
      bringToFront("password");
      return;
    }

    // No code required → navigate depending on external vs internal
    if (isExternalHref(project.href)) {
      window.open(project.href, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = project.href;
    }
  };

  const handlePasswordRequestClose = () => {
    setPasswordCloseKey((k) => k + 1);
  };

  const handlePasswordClosed = () => {
    setPasswordOpen(false);
    setPendingProject(null);
  };

  const handlePasswordSuccess = () => {
    if (!pendingProject || !pendingProject.href) return;

    const href = pendingProject.href;

    if (isExternalHref(href)) {
      // Open external project in new tab
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      // Internal route → same tab
      window.location.href = href;
    }

    setPasswordCloseKey((k) => k + 1);
  };

  // ──────────────────────────────────────────────
  // Video player handlers
  // ──────────────────────────────────────────────

  const handleProjectVideoRequest = (project: ProjectDef) => {
    if (!project.videoSrc) return;

    setSelectedVideoProject(project);
    setVideoPlayerOffset(isMobile ? { x: 0, y: -10 } : { x: 0, y: -40 });
    setVideoPlayerOpen(true);
    bringToFront("videoPlayer");
  };

  const handleVideoPlayerRequestClose = () => {
    setVideoPlayerCloseKey((k) => k + 1);
  };

  const handleVideoPlayerClosed = () => {
    setVideoPlayerOpen(false);
    setSelectedVideoProject(null);
  };

  // ──────────────────────────────────────────────
  // Folder window handlers
  // ──────────────────────────────────────────────

  const handleOpenFolder = (folder: ProjectFolder, anchorRect: DOMRect) => {
    // If already open, just bring it to front
    if (openFolderWindows[folder.id]) {
      bringToFront(folder.id);
      return;
    }

    const usedOffsets: Offset[] = [];
    if (!isMobile) {
      if (aboutOpen && aboutOffset) usedOffsets.push(aboutOffset);
      if (projectsOpen && projectsOffset) usedOffsets.push(projectsOffset);
      if (contactFormOpen && contactOffset) usedOffsets.push(contactOffset);
      if (followOpen && followOffset) usedOffsets.push(followOffset);
      Object.values(openFolderWindows).forEach((fw) => usedOffsets.push(fw.offset));
    }

    const offset = getOffset(isMobile, usedOffsets);
    const newZ = nextZ + 1;
    setNextZ(newZ);

    setOpenFolderWindows((prev) => ({
      ...prev,
      [folder.id]: {
        folder,
        anchorRect,
        offset,
        zIndex: newZ,
        closeKey: 0,
        selectedProjectId:
          folder.projects.find((item) => !isFolder(item))?.id ?? null,
      },
    }));
  };

  const handleFolderWindowClose = (folderId: string) => {
    setOpenFolderWindows((prev) => {
      const entry = prev[folderId];
      if (!entry) return prev;
      return { ...prev, [folderId]: { ...entry, closeKey: entry.closeKey + 1 } };
    });
  };

  const handleFolderWindowClosed = (folderId: string) => {
    setOpenFolderWindows((prev) => {
      const next = { ...prev };
      delete next[folderId];
      return next;
    });
  };

  const handleFolderSelectProject = (folderId: string, projectId: string) => {
    setOpenFolderWindows((prev) => {
      const entry = prev[folderId];
      if (!entry) return prev;
      return { ...prev, [folderId]: { ...entry, selectedProjectId: projectId } };
    });
  };

  // ──────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────

  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100dvh",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        background: theme.colors.BgAlt,
        fontFamily: theme.fonts.body,
      }}
    >
      {/* Puzzle scene – only the 3D content is offset vertically */}
      <div
        style={{
          position: "absolute",
          inset: 0,
        }}
      >
        <Canvas orthographic shadows camera={{ position: [...CAMERA_POS], zoom: CAMERA_ZOOM_START }}>
          <CameraZoom solved={isSolved} />
          <PuzzleScene
            solved={isSolved}
            solving={isSolving}
            onProximityChange={setProximity}
            onSolveBegin={() => setIsSolving(true)}
            onSolved={() => {
              setIsSolved(true);
              setIsSolving(false);
            }}
          />
        </Canvas>
      </div>

      {/* Center overlay area (proximity + nav) */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: isMobile ? NAV_BOTTOM_MOBILE : NAV_BOTTOM_DESKTOP,
          transform: "translateX(-50%)",
          minWidth: 260,
          minHeight: 48,
          pointerEvents: "none",
        }}
      >
        {/* Proximity text */}
        <div
  style={{
    position: "absolute",
    left: "50%",
    top: 0,
    transform: "translateX(-50%)",
    // No padding / pill bg – pure text
    padding: 0,
    borderRadius: 0,
    background: "transparent",
    color: theme.colors.Text,
    fontSize: 14,
    fontFamily: theme.fonts.body,
    whiteSpace: "nowrap",
    opacity: isSolved ? 0 : 1,
    pointerEvents: isSolved ? "none" : "auto",
    transition: "opacity 0.4s ease",
  }}
>
  {proximity > 0.8
    ? "Hold it there…"
    : proximity > 0.4
    ? "Getting close…"
    : "Drag to solve the puzzle"}
</div>

        {/* Nav buttons */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: 0,
            transform: "translateX(-50%)",
            marginTop: isMobile
              ? NAV_MARGIN_TOP_MOBILE
              : NAV_MARGIN_TOP_DESKTOP,
            opacity: isSolved ? 1 : 0,
            pointerEvents: isSolved ? "auto" : "none",
            transition: "opacity 0.6s ease 0.2s",
          }}
        >
          <div
            className="nav-bar"
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent: "center",
              gap: isMobile ? NAV_GAP_MOBILE : NAV_GAP_DESKTOP,
            }}
          >
            {/* About */}
            <NavButton
              label="About"
              selected={aboutSelected}
              buttonRef={(el) => {
                aboutBtnRef.current = el;
              }}
              onClick={handleAboutClick}
            />

            {/* Projects */}
            <NavButton
              label="Projects"
              selected={projectsSelected}
              buttonRef={(el) => {
                projectsBtnRef.current = el;
              }}
              onClick={handleProjectsClick}
            />

            {/* Contact → opens both Contact + Follow windows */}
            <NavButton
              label="Contact"
              selected={contactSelected}
              buttonRef={(el) => {
                contactBtnRef.current = el;
              }}
              onClick={handleContactClick}
            />
          </div>
        </div>
      </div>

      {/* ABOUT window */}
      {aboutOpen && (
        <Win95Window
          title="About"
          iconSrc="/images/Icon-About.svg"
          iconAlt="About"
          zIndex={aboutZ}
          anchorRect={aboutAnchor}
          targetOffset={aboutOffset}
          closeRequestKey={aboutCloseKey}
          onRequestClose={() => setAboutCloseKey((k) => k + 1)}
          onClosed={() => {
            setAboutOpen(false);
            setAboutSelected(false);
          }}
          onFocus={() => bringToFront("about")}
          windowSize={{
            desktopWidth: 500,
            desktopMaxHeight: "600px",
            mobileWidth: "92vw",
            mobileHeight: "70vh",
          }}
        >
          <AboutPanel
            imageSrc="/images/bros.png"
            imageAlt="Studio avatar"
          />
        </Win95Window>
      )}

      {/* PROJECTS window */}
      {projectsOpen && (
        <Win95Window
          title="Projects"
          iconSrc="/images/Icon-Projects.svg"
          iconAlt="Projects"
          zIndex={projectsZ}
          anchorRect={projectsAnchor}
          targetOffset={projectsOffset}
          closeRequestKey={projectsCloseKey}
          onRequestClose={() => setProjectsCloseKey((k) => k + 1)}
          onClosed={() => {
            setProjectsOpen(false);
            setProjectsSelected(false);
          }}
          onFocus={() => bringToFront("projects")}
          windowSize={{
            desktopWidth: 560,
            desktopMaxHeight: "520px",
            mobileWidth: "96vw",
            mobileHeight: "70vh",
          }}
        >
          <ProjectsPanel
            projects={PROJECTS}
            selectedId={selectedProjectId}
            onSelect={(id) => setSelectedProjectId(id)}
            onRequestPlay={handleProjectPlayRequest}
            onRequestPlayVideo={handleProjectVideoRequest}
            onOpenFolder={handleOpenFolder}
          />
        </Win95Window>
      )}

      {/* CONTACT FORM window */}
      {contactFormOpen && (
        <Win95Window
          title="Contact"
          iconSrc="/images/Icon-Contact.svg"
          iconAlt="Contact"
          zIndex={contactZ}
          anchorRect={contactAnchor}
          targetOffset={contactOffset}
          closeRequestKey={contactCloseKey}
          onRequestClose={() => {
            setContactCloseKey((k) => k + 1);
            setFollowCloseKey((k) => k + 1);
          }}
          onClosed={() => {
            setContactFormOpen(false);
            setContactSelected(false);
          }}
          onFocus={() => bringToFront("contact")}
          windowSize={{
            desktopWidth: 520,
            desktopMaxHeight: "360px",
            mobileWidth: "92vw",
            mobileHeight: "60vh",
          }}
        >
          <ContactPanel />
        </Win95Window>
      )}

      {/* FOLLOW US window */}
      {followOpen && (
        <Win95Window
          title="Connect"
          iconSrc="/images/Icon-Connect.svg"
          iconAlt="Follow"
          zIndex={followZ}
          anchorRect={followAnchor}
          targetOffset={followOffset}
          closeRequestKey={followCloseKey}
          onRequestClose={() => {
            setContactCloseKey((k) => k + 1);
            setFollowCloseKey((k) => k + 1);
          }}
          onClosed={() => {
            setFollowOpen(false);
            setContactSelected(false);
          }}
          onFocus={() => bringToFront("follow")}
          windowSize={{
            desktopWidth: 500,
            desktopMaxHeight: "220px",
            mobileWidth: "92vw",
            mobileHeight: "40vh",
          }}
        >
          <FollowPanel />
        </Win95Window>
      )}

      {/* PASSWORD window */}
      {passwordOpen && pendingProject && (
        <Win95Window
          title="Password"
          iconSrc="/images/Icon-Password.svg"
          iconAlt="Password"
          zIndex={passwordZ}
          anchorRect={passwordAnchor}
          targetOffset={passwordOffset}
          closeRequestKey={passwordCloseKey}
          onRequestClose={handlePasswordRequestClose}
          onClosed={handlePasswordClosed}
          onFocus={() => bringToFront("password")}
          windowSize={{
            desktopWidth: 360,
            desktopMaxHeight: "175px",
            mobileWidth: "80vw",
            mobileHeight: "40vh",
          }}
        >
          <PasswordPanel
            projectName={pendingProject.name}
            projectHref={pendingProject.href!}
            codeHash={pendingProject.codeHash}
            onSuccess={handlePasswordSuccess}
          />
        </Win95Window>
      )}

      {/* VIDEO PLAYER window */}
      {videoPlayerOpen && selectedVideoProject && (
        <Win95Window
          title="Video Player"
          iconSrc="/images/Icon-Disc.svg"
          iconAlt="Video Player"
          zIndex={videoPlayerZ}
          anchorRect={videoPlayerAnchor}
          targetOffset={videoPlayerOffset}
          closeRequestKey={videoPlayerCloseKey}
          onRequestClose={handleVideoPlayerRequestClose}
          onClosed={handleVideoPlayerClosed}
          onFocus={() => bringToFront("videoPlayer")}
          windowSize={{
            desktopWidth: 720,
            desktopMaxHeight: "auto",
            mobileWidth: "92vw",
            mobileHeight: "auto",
          }}
        >
          <VideoPlayerPanel project={selectedVideoProject} />
        </Win95Window>
      )}

      {/* FOLDER windows */}
      {Object.values(openFolderWindows).map((fw) => (
        <Win95Window
          key={fw.folder.id}
          title={fw.folder.name}
          iconSrc={fw.folder.iconSrc}
          iconAlt={fw.folder.name}
          zIndex={fw.zIndex}
          anchorRect={fw.anchorRect}
          targetOffset={fw.offset}
          closeRequestKey={fw.closeKey}
          onRequestClose={() => handleFolderWindowClose(fw.folder.id)}
          onClosed={() => handleFolderWindowClosed(fw.folder.id)}
          onFocus={() => bringToFront(fw.folder.id)}
          windowSize={{
            desktopWidth: 560,
            desktopMaxHeight: "520px",
            mobileWidth: "96vw",
            mobileHeight: "70vh",
          }}
        >
          <ProjectsPanel
            projects={fw.folder.projects}
            selectedId={fw.selectedProjectId}
            onSelect={(id) => handleFolderSelectProject(fw.folder.id, id)}
            onRequestPlay={handleProjectPlayRequest}
            onRequestPlayVideo={handleProjectVideoRequest}
            onOpenFolder={handleOpenFolder}
          />
        </Win95Window>
      ))}
    </div>
  );
}
